import React, {ReactElement, useEffect, useRef, useState} from "react";
import {Dropdown} from "@zendeskgarden/react-dropdowns";
import {TooltipModal} from "@zendeskgarden/react-modals";

import downChevron from "../../../../../../../assets/icons/chevron/down.svg";
import MonthPicker, {MonthPickerType} from "../../month-picker/MonthPicker";
import {Property} from "../../../../../../../contexts/properties/PropertiesContext";
import {MONTHS} from "../../../../../../../constants/Months";

import * as css from "../styles/css.module.scss";
import * as monthlyAvgCss from "../styles/monthly-average-menu/css.module.scss";
import {splitDate} from "../../../../../../../global/date_functions";

function startEndForProperty(property: Property): {startMonth: number, endMonth: number, startYear: number, endYear: number} {
    let startMonth = 0;
    let endMonth = 11;
    let startYear = 0;
    let endYear = 0;

    if(property.isAcquisitionProperty && property.acquisitionBudgetStartMonth && property.acquisitionBudgetYear) {
        endMonth = property.acquisitionBudgetStartMonth - 1;
        endYear = property.acquisitionBudgetYear;
        startMonth = property.acquisitionBudgetStartMonth;
        startYear = property.acquisitionBudgetYear - 1;
    } else {
        endMonth = property.reforecastStartMonthIndex - 1;
        endYear = property.reforecastYear;
        startMonth = property.reforecastStartMonthIndex;
        startYear = property.reforecastYear - 1;
    }

    if(endMonth < 0) {
        endMonth = 11;
        endYear = endYear - 1;
    }

    return {
        startMonth: startMonth,
        endMonth: endMonth,
        startYear: startYear,
        endYear: endYear
    };
}

function addMonths(year: number, month: number, toAddMonths: number): [number, number] {
    if(toAddMonths < 0) {
        const pMDiff = -toAddMonths;
        const ySub = Math.floor(pMDiff / 12);
        const mSub = pMDiff % 12;

        let newYear = year - ySub;
        let newMonth = month - mSub + 1;
        if(newMonth <= 0) {
            newMonth += 12;
            newYear -= 1;
        }
        return [newYear, newMonth - 1];
    } else {
        const yAdd = Math.floor(toAddMonths / 12);
        const mAdd = toAddMonths % 12;

        let newYear = year + yAdd;
        let newMonth = month + mAdd + 1;
        if(newMonth > 12) {
            newMonth -= 12;
            newYear += 1;
        }
        return [newYear, newMonth - 1];
    }
}

function dateDiff(first: [number, number], second: [number, number]): number {
    const [firstYear, firstMonth] = first;
    const [secondYear, secondMonth] = second;

    return (secondYear - firstYear) * 12 + (secondMonth - firstMonth);
}

function mapOutputDateForProperty(year: number, month: number, property: Property): [number, number] {
    if(property.isAcquisitionProperty && property.acquisitionBudgetStartMonth && property.acquisitionBudgetYear) {
        // Acquisition properties always have a full 12 month reforecast year (i.e. reforcast starts in Jan).
        // So, `{reforcastYear - 1}-12-01` == `{acquisitionBudgetYear}-{acquistionBudgetStartMonth - 1}-01`
        const actualsEndYear = property.reforecastYear - 1;
        const actualsEndMonth = 11;
        let acquisitionActualsEndYear = property.acquisitionBudgetYear;
        let acquisitionActualsEndMonth = property.acquisitionBudgetStartMonth - 1;
        if(acquisitionActualsEndMonth < 0) {
            acquisitionActualsEndMonth = 11;
            acquisitionActualsEndYear -= 1;
        }

        const mDiff = dateDiff([acquisitionActualsEndYear, acquisitionActualsEndMonth], [year, month]);
        return addMonths(actualsEndYear, actualsEndMonth, mDiff);
    }
    return [year, month];
}

function mapInputDateForProperty(year: number, month: number, property: Property): [number, number] {
    if(property && property.isAcquisitionProperty && property.acquisitionBudgetStartMonth && property.acquisitionBudgetYear) {
        // Acquisition properties always have a full 12 month reforecast year (i.e. reforcast starts in Jan).
        // So, `{reforcastYear - 1}-12-01` == `{acquisitionBudgetYear}-{acquistionBudgetStartMonth - 1}-01`
        const actualsEndYear = property.reforecastYear - 1;
        const actualsEndMonth = 11;
        let acquisitionActualsEndYear = property.acquisitionBudgetYear;
        let acquisitionActualsEndMonth = property.acquisitionBudgetStartMonth - 1;
        if(acquisitionActualsEndMonth < 0) {
            acquisitionActualsEndMonth = 11;
            acquisitionActualsEndYear -= 1;
        }

        const mDiff = dateDiff([actualsEndYear, actualsEndMonth], [year, month]);
        return addMonths(acquisitionActualsEndYear, acquisitionActualsEndMonth, mDiff);
    }
    return [year, month];
}

interface IOperationalMonthlyAverageRangeMenu {
    property: Property;
    // Dates are ISO8601 (e.g. 2025-01-01)
    startMonthDate?: string;
    endMonthDate?: string;
    startMonthDateSet: (startMonth: string) => void;
    endMonthDateSet: (endMonth: string) => void;
    config?: {
        maxMonth: {
            month: number
            year: number
        },
        minMonth: {
            month: number
            year: number
        }
    }
    triggerNode?: ReactElement;
}

export default function MonthlyRangeMenu({
    property,
    startMonthDate,
    endMonthDate,
    startMonthDateSet,
    endMonthDateSet,
    config,
    triggerNode
}: IOperationalMonthlyAverageRangeMenu): React.ReactElement {
    const [startMonth, setStartMonth] = useState<number>(0);
    const [endMonth, setEndMonth] = useState<number>(11);
    const [startYear, setStartYear] = useState<number>(2000);
    const [endYear, setEndYear] = useState<number>(2000);
    const [refElement, setRefElement] = useState<HTMLDivElement|null>(null);
    const [startPeriodRefElement, setStartPeriodRefElement] = useState<HTMLDivElement|null>(null);
    const [endPeriodRefElement, setEndPeriodRefElement] = useState<HTMLDivElement|null>(null);

    const triggerRef = useRef<HTMLDivElement>(null);
    const startPeriodRef = useRef<HTMLDivElement>(null);
    const endPeriodRef = useRef<HTMLDivElement>(null);

    /**
     * Default the month-picker selections to what's coming from the backend
     */
    useEffect(() => {
        let startMonth = 0;
        let startYear = 2000;
        let endMonth = 11;
        let endYear = 2000;

        if(property) {
            const propData = startEndForProperty(property);
            startMonth = propData.startMonth;
            startYear = propData.startYear;
            endMonth = propData.endMonth;
            endYear = propData.endYear;
        }
        if(startMonthDate) {
            const [year, month] = splitDate(startMonthDate);
            if(property && property.isAcquisitionProperty) {
                const [mappedYear, mappedMonth] = mapInputDateForProperty(year, month - 1, property);
                startYear = mappedYear;
                startMonth = mappedMonth;
            } else {
                startYear = year;
                startMonth = month - 1;
            }
        }

        if(endMonthDate) {
            const [year, month] = splitDate(endMonthDate);
            if(property && property.isAcquisitionProperty) {
                const [mappedYear, mappedMonth] = mapInputDateForProperty(year, month - 1, property);
                endYear = mappedYear;
                endMonth = mappedMonth;
            } else {
                endYear = year;
                endMonth = month - 1;
            }
        }

        setStartMonth(startMonth);
        setEndMonth(endMonth);
        setStartYear(startYear);
        setEndYear(endYear);
    }, [
        startMonthDate,
        endMonthDate,
        property
    ]);

    const handleSetStartMonth = (month: number, year: number) => {
        setStartMonth(month);
        if(property && property.isAcquisitionProperty) {
            const [mappedYear, mappedMonth] = mapOutputDateForProperty(year, month, property);
            startMonthDateSet(`${mappedYear}-${(mappedMonth + 1).toString().padStart(2, "0")}-01`);
        } else {
            startMonthDateSet(`${year}-${(month + 1).toString().padStart(2, "0")}-01`);
        }
        setStartPeriodRefElement(null);
    };

    const handleSetEndMonth = (month: number, year: number) => {
        setEndMonth(month);
        if(property && property.isAcquisitionProperty) {
            const [mappedYear, mappedMonth] = mapOutputDateForProperty(year, month, property);
            endMonthDateSet(`${mappedYear}-${(mappedMonth + 1).toString().padStart(2, "0")}-01`);
        } else {
            endMonthDateSet(`${year}-${(month + 1).toString().padStart(2, "0")}-01`);
        }
        setEndPeriodRefElement(null);
    };

    const handleRootClick = () => {
        setRefElement(triggerRef.current);
    };

    const handleStartPeriodClick = () => {
        setEndPeriodRefElement(null);
        // To remove the overlap period when both popovers are visible
        setTimeout(() => setStartPeriodRefElement(startPeriodRef.current), 200);
    };

    const handleEndPeriodClick = () => {
        setStartPeriodRefElement(null);
        // To remove the overlap period when both popovers are visible
        setTimeout(() => setEndPeriodRefElement(endPeriodRef.current), 200);
    };

    const handleCloseRoot = () => {
        setRefElement(null);
    };

    const handleCloseStartPeriodPopover = () => {
        setStartPeriodRefElement(null);
    };

    const handleCloseEndPeriodPopover = () => {
        setEndPeriodRefElement(null);
    };

    if (!property || !startYear || !endYear) {
        return <></>;
    }

    let minMonth = 0;
    let maxMonth = property.reforecastStartMonthIndex - 1;
    let minYear = property.reforecastYear - 1;
    let maxYear = property.reforecastYear;
    if(config) {
        minMonth = config.minMonth.month;
        minYear = config.minMonth.year;
        maxMonth = config.maxMonth.month;
        maxYear = config.maxMonth.year;
    } else if(property.isAcquisitionProperty && property.acquisitionBudgetStartMonth != null && property.acquisitionBudgetYear) {
        minMonth = property.acquisitionBudgetStartMonth;
        maxMonth = minMonth == 0 ? 11 : minMonth - 1;
        maxYear = minMonth == 0 ? property.acquisitionBudgetYear - 1 : property.acquisitionBudgetYear;
        minYear = minMonth == 0 ? maxYear : maxYear - 1;
    }

    const chevronDownIcon = <img className={monthlyAvgCss.chevDown} src={downChevron} alt="down" width="12px" height="12px" />;

    const rootDropdown = (
            <Dropdown
                    isOpen
                    onSelect={() => null}
                    downshiftProps={{defaultHighlightedIndex: 0}}
            >
                <div className={css.menuWrapper}>
                    <div className={monthlyAvgCss.menuHeader}>
                        Select months to average
                    </div>
                    <div className={`${monthlyAvgCss.row} ${monthlyAvgCss.radioRow} ${monthlyAvgCss.basePeriodRadioRow}`}>
                        <div className={monthlyAvgCss.dateRangeLabel}>
                            Average actuals from&nbsp;
                        </div>
                        <div className={monthlyAvgCss.monthPickerTarget}>
                            <div
                                    className={css.triggerNode}
                                    ref={startPeriodRef}
                                    onClick={handleStartPeriodClick}
                                    style={inlineStyles.triggerNode}
                            >
                                {`${MONTHS[startMonth]} ${property.isAcquisitionProperty ? 'T12' : startYear}`}
                                {chevronDownIcon}
                            </div>
                            <div className={monthlyAvgCss.monthPickerTargetChild}>
                                <TooltipModal
                                        className={css.tooltipModal}
                                        hasArrow={false}
                                        referenceElement={startPeriodRefElement}
                                        onClose={handleCloseStartPeriodPopover}
                                        placement="bottom"
                                        isAnimated
                                >
                                    <TooltipModal.Body className={css.tooltipModalBody}>
                                        <div className={`${css.menuWrapper} ${monthlyAvgCss.menuWrapper}`}>
                                            <Dropdown
                                                    isOpen
                                                    onSelect={() => null}
                                                    downshiftProps={{defaultHighlightedIndex: 0}}
                                            >
                                                <div style={inlineStyles.rootMenu}>
                                                    <MonthPicker
                                                            minMonth={minMonth}
                                                            maxMonth={endMonth - 1}
                                                            minYear={minYear}
                                                            maxYear={endYear}
                                                            defaultMonth={startMonth}
                                                            defaultYear={startYear}
                                                            setMonth={handleSetStartMonth}
                                                            setYear={setStartYear}
                                                            type={MonthPickerType.FROM}
                                                            property={property}
                                                    />
                                                </div>
                                            </Dropdown>
                                        </div>
                                    </TooltipModal.Body>
                                </TooltipModal>
                            </div>
                        </div>
                        <div className={monthlyAvgCss.dateRangeLabel}>
                            &nbsp;to&nbsp;
                        </div>
                        <div className={monthlyAvgCss.monthPickerTarget}>
                            <div
                                    className={css.triggerNode}
                                    ref={endPeriodRef}
                                    onClick={handleEndPeriodClick}
                                    style={inlineStyles.triggerNode}
                            >
                                {`${MONTHS[endMonth]} ${property.isAcquisitionProperty ? 'T12' : endYear}`}
                                {chevronDownIcon}
                            </div>
                            <div className={monthlyAvgCss.monthPickerTargetChild}>
                                <TooltipModal
                                        className={css.tooltipModal}
                                        hasArrow={false}
                                        referenceElement={endPeriodRefElement}
                                        onClose={handleCloseEndPeriodPopover}
                                        placement="bottom"
                                        isAnimated
                                >
                                    <TooltipModal.Body className={css.tooltipModalBody}>
                                        <div className={`${css.menuWrapper} ${monthlyAvgCss.menuWrapper}`}>
                                            <Dropdown
                                                    isOpen
                                                    onSelect={() => null}
                                                    downshiftProps={{defaultHighlightedIndex: 0}}
                                            >
                                                <div style={inlineStyles.rootMenu}>
                                                    <MonthPicker
                                                            minMonth={startMonth + 1}
                                                            maxMonth={maxMonth}
                                                            minYear={startYear}
                                                            maxYear={maxYear}
                                                            defaultMonth={endMonth}
                                                            defaultYear={endYear}
                                                            setMonth={handleSetEndMonth}
                                                            setYear={setEndYear}
                                                            type={MonthPickerType.TO}
                                                            property={property}
                                                    />
                                                </div>
                                            </Dropdown>
                                        </div>
                                    </TooltipModal.Body>
                                </TooltipModal>
                            </div>
                        </div>
                    </div>
                </div>
            </Dropdown>
    );

    if (triggerNode) {
        return (
                <>
                    <div
                            className={css.triggerNode}
                            ref={triggerRef}
                            onClick={handleRootClick}
                    >
                        {triggerNode}
                    </div>
                    <TooltipModal
                            className={css.tooltipModal}
                            hasArrow={false}
                            referenceElement={refElement}
                            onClose={handleCloseRoot}
                            placement="bottom"
                            isAnimated
                    >
                        <TooltipModal.Body className={css.tooltipModalBody}>
                            {rootDropdown}
                        </TooltipModal.Body>
                    </TooltipModal>
                </>
        );
    }
    return rootDropdown;
}

/**
 * To override ZDG styles and avoid using !important all the time
 */
const inlineStyles = ({
    rootMenu: {marginTop: "30px", position: "absolute" as 'absolute', transform: "none", width: "235px"},
    select: {backgroundColor: "transparent", border: "none", minWidth: "65px"},
    triggerNode: {cursor: "pointer"},
});
